import React from 'react';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { getStatusActions } from './utils';
import { adjustOpacity } from '../../../../theme/utils';

/**
 * ExperimentActionsMenu Component
 *
 * Actions menu for experiment row.
 *
 * @param {Object} props
 * @param {Object} props.anchorEl - Anchor element for menu
 * @param {Object} props.selectedExperiment - Selected experiment
 * @param {Array} props.experiments - All experiments (for checking active experiments)
 * @param {Function} props.onClose - Handler for closing menu
 * @param {Function} props.onMenuAction - Handler for menu action
 */
function ExperimentActionsMenu({
  anchorEl,
  selectedExperiment,
  experiments,
  onClose,
  onMenuAction,
}) {
  const theme = useTheme();
  const statusActions =
    selectedExperiment && experiments
      ? getStatusActions(selectedExperiment, experiments)
      : [];

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={e => e.stopPropagation()}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      TransitionProps={{
        timeout: 100,
      }}
      slotProps={{
        paper: {
          sx: {
            transition: 'all 0.1s ease-in-out',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            mt: 0.5,
          },
        },
      }}
    >
      <MenuItem
        onClick={() => onMenuAction('edit')}
        sx={{
          '&:hover': {
            backgroundColor: adjustOpacity(theme.palette.primary.main, 0.1),
          },
        }}
      >
        <EditIcon
          sx={{
            mr: 1.5,
            fontSize: '1.25rem',
            color: theme.palette.primary.main,
          }}
        />
        <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
          Edit Experiment
        </Typography>
      </MenuItem>
      {statusActions.map((statusAction, idx) => {
        let actionColor = theme.palette.text.secondary;
        let iconColor = theme.palette.text.secondary;
        let hoverColor = adjustOpacity(theme.palette.text.secondary, 0.1);

        if (
          statusAction.action === 'start' ||
          statusAction.action === 'resume'
        ) {
          actionColor = theme.palette.success.main;
          iconColor = theme.palette.success.main;
          hoverColor = adjustOpacity(theme.palette.success.main, 0.1);
        } else if (statusAction.action === 'pause') {
          actionColor = theme.palette.warning.main;
          iconColor = theme.palette.warning.main;
          hoverColor = adjustOpacity(theme.palette.warning.main, 0.1);
        } else if (statusAction.action === 'stop') {
          actionColor = theme.palette.text.secondary;
          iconColor = theme.palette.text.secondary;
          hoverColor = adjustOpacity(theme.palette.text.secondary, 0.1);
        }

        return (
          <Tooltip
            key={idx}
            title={statusAction.tooltip || ''}
            placement="left"
          >
            <span>
              <MenuItem
                onClick={() => onMenuAction(statusAction.action)}
                disabled={statusAction.disabled}
                sx={{
                  '&:hover': {
                    backgroundColor: hoverColor,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    mr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {React.cloneElement(statusAction.icon, {
                    sx: {
                      fontSize: '1.25rem',
                      color: iconColor,
                    },
                  })}
                </Box>
                <Typography sx={{ color: actionColor, fontWeight: 500 }}>
                  {statusAction.label}
                </Typography>
              </MenuItem>
            </span>
          </Tooltip>
        );
      })}
    </Menu>
  );
}

export default ExperimentActionsMenu;
